import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    VStack,
    Input,
    Button,
    Text,
    Image,
    Heading,
    Flex,
    Separator,
    Stack
} from '@chakra-ui/react';
import { Field } from '../components/ui/field';
import { toaster } from '../components/ui/toaster';
import { FiMail, FiLock } from 'react-icons/fi';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const response = await login(email, password);

            if (response.profiles) {
                navigate('/select-profile', { state: { profiles: response.profiles } });
            } else {
                navigate('/');
            }
        } catch (err) {
            let errorMessage = 'Failed to log in. Please check your credentials.';

            if (err.response?.data) {
                if (Array.isArray(err.response.data.detail)) {
                    errorMessage = err.response.data.detail
                        .map(e => e.msg || JSON.stringify(e))
                        .join(', ');
                } else if (typeof err.response.data.detail === 'string') {
                    errorMessage = err.response.data.detail;
                } else if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                }
            }

            toaster.create({
                title: 'Login Failed',
                description: errorMessage,
                type: 'error',
                duration: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Flex h="100vh" bgGradient="to-br" gradientFrom="blue.50" gradientTo="purple.50">
            {/* Left Side - Branding */}
            <Flex
                flex="1"
                display={{ base: 'none', md: 'flex' }}
                align="center"
                justify="center"
                bg="blue.600"
                bgGradient="to-br"
                gradientFrom="blue.500"
                gradientTo="purple.600"
                position="relative"
                overflow="hidden"
            >
                {/* Decorative elements */}
                <Box
                    position="absolute"
                    top="-10%"
                    right="-10%"
                    w="500px"
                    h="500px"
                    borderRadius="full"
                    bg="whiteAlpha.100"
                />
                <Box
                    position="absolute"
                    bottom="-15%"
                    left="-10%"
                    w="600px"
                    h="600px"
                    borderRadius="full"
                    bg="whiteAlpha.100"
                />

                <VStack spacing={6} zIndex={1} px={12}>
                    <Box
                        w="200px"
                        h="auto"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Image
                            src="/ReliamindsLogoWhiteTransparent.png"
                            alt="Reliaminds Logo"
                            w="100%"
                            h="auto"
                        />
                    </Box>
                    <Heading color="white" size="2xl" textAlign="center">
                        Tutor Management System
                    </Heading>
                    <Text color="whiteAlpha.900" fontSize="lg" textAlign="center" maxW="md">
                        Streamline your tutoring operations and boost team productivity
                    </Text>
                </VStack>
            </Flex>

            {/* Right Side - Login Form */}
            <Flex
                flex={{ base: '1', md: '0.7' }}
                align="center"
                justify="center"
                p={8}
            >
                <Box
                    w="full"
                    maxW="450px"
                    bg="white"
                    borderRadius="2xl"
                    boxShadow="2xl"
                    p={10}
                    borderWidth="1px"
                    borderColor="gray.200"
                >
                    {/* Mobile Logo */}
                    <VStack spacing={2} mb={8} display={{ base: 'flex', md: 'none' }}>
                        <Box
                            w="150px"
                            h="auto"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                            borderRadius="xl"
                            p={3}
                        >
                            <Image
                                src="/ReliamindsLogoWhiteTransparent.png"
                                alt="Reliaminds Logo"
                                w="100%"
                                h="auto"
                            />
                        </Box>
                    </VStack>

                    <VStack spacing={2} mb={8} align="stretch">
                        <Heading
                            as="h1"
                            size="xl"
                            bgGradient="to-r"
                            gradientFrom="blue.600"
                            gradientTo="purple.600"
                            bgClip="text"
                            fontWeight="extrabold"
                        >
                            Welcome!
                        </Heading>
                        <Text color="gray.600" fontSize="md">
                            Sign in to your account to continue
                        </Text>
                    </VStack>

                    <VStack as="form" onSubmit={handleLogin} spacing={5} align="stretch">
                        <Field label="Email Address" required width="full">
                            <Box position="relative" width="full">
                                <Box
                                    as="span"
                                    position="absolute"
                                    left="12px"
                                    top="50%"
                                    transform="translateY(-50%)"
                                    zIndex={1}
                                    color="gray.400"
                                >
                                    <FiMail />
                                </Box>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    size="lg"
                                    borderRadius="lg"
                                    pl="40px"
                                    width="full"
                                    _focus={{
                                        borderColor: 'blue.500',
                                        boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
                                    }}
                                />
                            </Box>
                        </Field>

                        <Field label="Password" required width="full">
                            <Box position="relative" width="full">
                                <Box
                                    as="span"
                                    position="absolute"
                                    left="12px"
                                    top="50%"
                                    transform="translateY(-50%)"
                                    zIndex={1}
                                    color="gray.400"
                                >
                                    <FiLock />
                                </Box>
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    size="lg"
                                    borderRadius="lg"
                                    pl="40px"
                                    width="full"
                                    _focus={{
                                        borderColor: 'blue.500',
                                        boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
                                    }}
                                />
                            </Box>
                        </Field>

                        <Button
                            type="submit"
                            size="lg"
                            width="full"
                            mt={4}
                            borderRadius="lg"
                            fontWeight="bold"
                            loading={loading}
                            loadingText="Signing in..."
                            bgGradient="to-r"
                            gradientFrom="teal.500"
                            gradientTo="cyan.600"
                            color="white"
                            _hover={{
                                bgGradient: 'to-r',
                                gradientFrom: 'teal.600',
                                gradientTo: 'cyan.700',
                                transform: 'translateY(-2px)',
                                boxShadow: 'lg',
                            }}
                            transition="all 0.2s"
                        >
                            Sign In
                        </Button>

                        <Separator my={4} />

                        <Text fontSize="sm" color="gray.600" textAlign="center">
                            Need help? Contact your system administrator
                        </Text>
                    </VStack>
                </Box>
            </Flex>
        </Flex>
    );
};

export default LoginPage;
